import { useCallback, useMemo, useRef } from 'react';
import { FileHandler } from '@tiptap/extension-file-handler';
import type { AnyExtension, Editor } from '@tiptap/core';
import { message } from 'antd';

import { createClipboardImageAdoption } from './clipboardImages';
import { createInlineImageExtension } from './InlineImageExtension';
import type { ImportImageUrlFn, UploadImageFn, UploadedImage } from './types';

export type { ImportImageUrlFn, UploadImageFn, UploadedImage };

/** `data-*` attribute used when the consumer does not name its own. */
export const DEFAULT_IMAGE_ID_ATTR = 'data-cam-image-id';

const UPLOADABLE_MIME_TYPES = [
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
];

export function insertUploadedImage(editor: Editor, uploaded: UploadedImage) {
	editor
		.chain()
		.focus()
		.insertContent({
			type: 'image',
			attrs: {
				src: uploaded.url,
				alt: uploaded.original_name || '',
				imageId: uploaded.id,
			},
		})
		.run();
}

export interface InlineImageSupport {
	/** Image node plus paste/drop upload, ready to spread into `extensions`. */
	extensions: AnyExtension[];
	/** Upload one file and insert it at the cursor (toolbar button, file picker). */
	uploadAndInsert: (file: File, editor: Editor | null) => Promise<void>;
	enabled: boolean;
}

/**
 * Inline-image wiring: the resizable image node, paste/drop upload, and
 * insertion at the cursor.
 *
 * Passing no upload function yields no extensions, which is how the editor
 * stays text-only for forms that have nowhere to put an image.
 */
export function useInlineImageSupport(
	upload: UploadImageFn | undefined,
	importUrl: ImportImageUrlFn | undefined,
	dataAttr: string
): InlineImageSupport {
	// Extensions are built once for the editor's lifetime, so the paste handlers
	// reach these through refs rather than capturing the ones that existed at
	// mount (they usually close over a record id set later).
	const uploadRef = useRef(upload);
	uploadRef.current = upload;
	const importUrlRef = useRef(importUrl);
	importUrlRef.current = importUrl;

	const uploadAndInsert = useCallback(
		async (file: File, editor: Editor | null) => {
			const uploadFn = uploadRef.current;
			if (!editor || !uploadFn || !file.type?.startsWith('image/')) return;
			try {
				const uploaded = await uploadFn(file);
				if (uploaded?.url) insertUploadedImage(editor, uploaded);
			} catch {
				message.error('Failed to upload image');
			}
		},
		[]
	);

	const enabled = Boolean(upload);

	const extensions = useMemo<AnyExtension[]>(
		() =>
			enabled
				? [
						createInlineImageExtension(dataAttr),
						createClipboardImageAdoption({
							dataAttr,
							getUpload: () => uploadRef.current,
							getImportUrl: () => importUrlRef.current,
						}),
						FileHandler.configure({
							allowedMimeTypes: UPLOADABLE_MIME_TYPES,
							// Copying an image from a browser, Word or Outlook puts both a
							// file and a `text/html` flavor on the clipboard. Left to its
							// default the handler lets ProseMirror also paste that HTML,
							// which embeds a second <img> pointing at a `blob:`, `data:` or
							// `file:///` src the server strips on save. Consuming the event
							// keeps only the copy we upload.
							consumePasteEvent: true,
							onPaste: (editor, files) =>
								files.forEach((file) => uploadAndInsert(file, editor)),
							onDrop: (editor, files) =>
								files.forEach((file) => uploadAndInsert(file, editor)),
						}),
				  ]
				: [],
		[enabled, dataAttr, uploadAndInsert]
	);

	return { extensions, uploadAndInsert, enabled };
}
